'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  type UseRadioProps,
  useRadio,
  useRadioGroup,
} from '@ironhack/design-system2/hooks';
import { Box } from '@ironhack/design-system2/components/box';
import { Button } from '@ironhack/design-system2/components/button';
import { Card } from '@ironhack/design-system2/components/card';
import { chakra } from '@ironhack/design-system2/components/chakra';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { Icon } from '@ironhack/design-system2/components/icon';
import {
  ListItem,
  UnorderedList,
} from '@ironhack/design-system2/components/list';
import { SelectCard } from '@ironhack/design-system2/components/select-card';
import { VStack } from '@ironhack/design-system2/components/stack';
import { Text } from '@ironhack/design-system2/components/text';
import { ArrowLeft } from '@ironhack/design-system2/icons/arrow-left';
import { ArrowRight } from '@ironhack/design-system2/icons/arrow-right';
import { HexagonBadge } from '@ironhack/design-system2/icons/hexagon-badge';
import { Home } from '@ironhack/design-system2/icons/home';
import { MapPin } from '@ironhack/design-system2/icons/map-pin';
import { Plus } from '@ironhack/design-system2/icons/plus';
import { Send } from '@ironhack/design-system2/icons/send';
import { Wifi } from '@ironhack/design-system2/icons/wifi';
import { filter, map, omit, pick, pipe, prop, replace } from 'ramda';
import { type ReactElement, useEffect, useState } from 'react';
import { Image, type StructuredTextGraphQlResponse } from 'react-datocms';
import { FormProvider, useForm } from 'react-hook-form';
import { useSessionStorage } from 'react-use';

import { CheckboxField } from '@/app/components/client/checkbox-field';
import { ContactDetailsFields } from '@/app/components/client/contact-details-fields';
import { FormThankYou } from '@/app/components/client/form-thank-you';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { PhoneField } from '@/app/components/client/phone-field';
import { StructuredContent } from '@/app/components/client/structured-content';
import { ListWithIcons } from '@/app/components/server/list-with-icons';
import { TrackBadge } from '@/app/components/server/track-badge';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { useFormRequest } from '@/hooks/use-form-request';
import { useGtmLocalStorage } from '@/hooks/use-gtm-local-storage';
import { useThankYouSession } from '@/hooks/use-thank-you-session';
import {
  type DatoButtonBlock,
  type DatoCampus,
  type DatoCourse,
  type DatoImage,
  type DatoListWithIconsBlock,
} from '@/lib/datocms/types';
import { contactFormTags, sendEvent } from '@/lib/gtm';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import { commonKeys, courseToBackground, getPrettyCohort } from '@/lib/utils';
import { type ValidationMessageSet } from '@/lib/utils/form-validations';
import {
  isFormSubmitTimeoutError,
  logSeatBookingSubmitFailure,
} from '@/lib/utils/form-submit-timeout';
import { getFullApplySchema } from '@/lib/utils/form-validations/forms/full-apply-form';
import { type EmailInput } from '@/lib/utils/form-validations/schemas/email-schema';
import {
  type NameInputs,
  type NameValidationMessages,
} from '@/lib/utils/form-validations/schemas/name-schema';
import {
  type PhoneInputs,
  getPhoneDefaults,
} from '@/lib/utils/form-validations/schemas/phone-schema';
import { isMastersActive } from '@/lib/utils/masters';
import { type Language } from '@/lib/utils/language';
import {
  type AvailableCourseTrack,
  type CourseFormat,
  type Region,
  type WebCohort,
} from '@/types';

export type FullApplyForm = {
  campusLabel: string;
  campusPlaceholder: string;
  campusRequiredMessage: string;
  buttonText: string;
  chooseBetween: string;
  completeFields: string;
  courseLabel: string;
  coursePlaceholder: string;
  courseRequiredMessage: string;
  courseTypeLabel: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailValidationMessages: ValidationMessageSet;
  errorMessage: string;
  timeoutErrorMessage: string;
  firstNameLabel: string;
  firstNamePlaceholder: string;
  firstNameValidationMessages: NameValidationMessages;
  inPersonOption: string;
  inquiryLabel: string;
  inquiryPlaceholder: string;
  inquiryRequiredMessage: string;
  lastNameLabel: string;
  lastNamePlaceholder: string;
  lastNameValidationMessages: NameValidationMessages;
  legalText: StructuredTextGraphQlResponse;
  newsletterLabel: string;
  onlineOption: string;
  phoneInvalidMessage: string;
  phoneLabel: string;
  phonePlaceholder: string;
  phoneRequiredMessage: string;
  phoneStep2Text: string;
  step1Cta: string;
  stepsTitleDynamic: string;
  backButtonText: string;
  nextButtonText: string;
  step2Title: string;
  step2Subtitle: string;
  step2Image: DatoImage;
  step3Title: string;
  step4Title: string;
  step4RequiredMessage: string;
  step4FullTimeCard: [
    {
      title: string;
      text: string;
      iconList: [DatoListWithIconsBlock];
    }
  ];
  step4PartTimeCard: [
    {
      title: string;
      text: string;
      iconList: [DatoListWithIconsBlock];
    }
  ];
  step5Title: string;
  step5ErrorMessage: string;
  step6Question: string;
  step6MoreDates: string;
  step6RequiredMessage: string;
  subtitle: string;
  thankYouText: string;
  thankYouTitle: string;
  title: string;
};

type FormData = {
  cToken: string;
  emailOptIn: boolean;
  phone: PhoneInputs;
  selection: {
    format: CourseFormat | '';
    track: AvailableCourseTrack | '';
    campus: string;
  };
} & EmailInput &
  NameInputs;

type Props = {
  campuses: Array<
    Pick<DatoCampus, 'code' | 'country' | 'isShownOnWebsite' | 'name'>
  >;
  courses: Array<Pick<DatoCourse, 'code' | 'name'>>;
  fullApplyForm: FullApplyForm;
  preStudentPortalButton: DatoButtonBlock | null;
  thankYouButton: [DatoButtonBlock];
} & FlexProps;

type CourseTrackRadioCardProps = UseRadioProps & { courseName: string };

const CourseTrackRadioCardField = (props: CourseTrackRadioCardProps) => {
  const { courseName, ...useRadioProps } = props;
  const { state, getInputProps, getRadioProps, htmlProps, getLabelProps } =
    useRadio(useRadioProps);

  const badgeColor =
    courseToBackground[useRadioProps.value as AvailableCourseTrack];

  return (
    <chakra.label
      {...htmlProps}
      cursor="pointer"
      position="relative"
      width="100%"
    >
      <input {...getInputProps()} />
      <SelectCard.Root
        {...getRadioProps()}
        isChecked={state.isChecked}
        w="100%"
      >
        <SelectCard.Header>
          <HexagonBadge
            color={`url(#${badgeColor})`}
            fill={`url(#${badgeColor})`}
            size="24px"
          />
          <Text flex={1} textStyle="lBold" {...getLabelProps()}>
            {courseName}
          </Text>
        </SelectCard.Header>
      </SelectCard.Root>
    </chakra.label>
  );
};

type CourseFormatRadioCardProps = UseRadioProps & {
  courseTrackName: string;
  cardData: [
    {
      title: string;
      text: string;
      iconList: [DatoListWithIconsBlock];
    }
  ];
};

const CourseFormatRadioCardField = (props: CourseFormatRadioCardProps) => {
  const { courseTrackName, cardData, ...useRadioProps } = props;
  const {
    title,
    text,
    iconList: [iconList],
  } = cardData[0];
  const { state, getInputProps, getRadioProps, htmlProps, getLabelProps } =
    useRadio(useRadioProps);

  return (
    <chakra.label
      {...htmlProps}
      cursor="pointer"
      position="relative"
      width="100%"
    >
      <input {...getInputProps()} />
      <SelectCard.Root
        {...getRadioProps()}
        isChecked={state.isChecked}
        w="100%"
      >
        <SelectCard.Header gap={2}>
          <TrackBadge
            format={useRadioProps.value}
            size={5}
            track={courseTrackName as AvailableCourseTrack}
          />
          <Text textStyle="lBold" {...getLabelProps()}>
            {title}
          </Text>
        </SelectCard.Header>
        <SelectCard.Body>
          <Flex color="text.secondary" direction="column" gap={3} mt={1.5}>
            <Text textStyle="m">{text}</Text>
            <VStack alignItems="start">
              <ListWithIcons
                iconProps={{ alignSelf: 'start', color: 'link.hover', mr: 1 }}
                list={iconList}
                mx={1}
                spacing={1.5}
              />
            </VStack>
          </Flex>
        </SelectCard.Body>
      </SelectCard.Root>
    </chakra.label>
  );
};

type CourseCampusRadioCardProps = UseRadioProps & {
  courseCampusName: string;
  courseCampusCode: string;
  courseCampusCountry: string;
};

const CourseCampusRadioCardField = (props: CourseCampusRadioCardProps) => {
  const {
    courseCampusName,
    courseCampusCode,
    courseCampusCountry,
    ...useRadioProps
  } = props;
  const { state, getInputProps, getRadioProps, htmlProps, getLabelProps } =
    useRadio(useRadioProps);

  return (
    <chakra.label
      {...htmlProps}
      cursor="pointer"
      position="relative"
      width="100%"
    >
      <input {...getInputProps()} />
      <SelectCard.Root
        {...getRadioProps()}
        isChecked={state.isChecked}
        w="100%"
      >
        <SelectCard.Header>
          <Icon as={courseCampusCode === 'rmt' ? Wifi : Home} boxSize="24px" />
          <Text textStyle="lBold" {...getLabelProps()}>
            {courseCampusCode === 'rmt'
              ? courseCampusCountry
              : courseCampusName}
          </Text>
        </SelectCard.Header>
      </SelectCard.Root>
    </chakra.label>
  );
};

type CourseSelectionRadioCardProps = UseRadioProps & {
  cohort: WebCohort;
  language: Language;
};

const CourseSelectionRadioCardField = (
  props: CourseSelectionRadioCardProps
) => {
  const { cohort, language, ...useRadioProps } = props;
  const { state, getInputProps, getRadioProps, htmlProps, getLabelProps } =
    useRadio(useRadioProps);

  const prettyCohort = getPrettyCohort(cohort, language);

  return (
    <chakra.label
      {...htmlProps}
      cursor="pointer"
      display="block"
      position="relative"
      width={['100%', null, 'auto']}
    >
      <input {...getInputProps()} />
      <SelectCard.Root
        {...getRadioProps()}
        isChecked={state.isChecked}
        w="full"
      >
        <SelectCard.Header>
          <Text textStyle="lBold" {...getLabelProps()}>
            {`${prettyCohort.startDate} - ${prettyCohort.endDate}`}
          </Text>
        </SelectCard.Header>
        <SelectCard.Body>
          <Text color="text.secondary" mb={0.5} mt={2} textStyle="mBold">
            {`${prettyCohort.weekDays.join(
              ` ${cohort.format === 'ft' ? '-' : '&'} `
            )}:
              ${prettyCohort.weekDayStartTime} - ${
              prettyCohort.weekDayEndTime
            } (${prettyCohort.timezone})`}
          </Text>
          {cohort.format === 'pt' && (
            <Text color="text.secondary" mb={0.5} textStyle="mBold">
              {`${prettyCohort.weekendDays.join(' & ')}:
           ${prettyCohort.weekendStartTime} - ${prettyCohort.weekendEndTime} (${
                prettyCohort.timezone
              })`}
            </Text>
          )}
          <Text color="text.secondary" textStyle="mBold">
            {`${prettyCohort.modality} - ${prettyCohort.language}`}
          </Text>
          <Flex className="price" gap={1} mt={2}>
            <Text textStyle="mBold">{`${prettyCohort.discountPrice}`}</Text>
            {prettyCohort.price !== prettyCohort.discountPrice && (
              <Text as="s" color="text.tertiary" textStyle="mBold">
                {`${prettyCohort.price}`}
              </Text>
            )}
          </Flex>
        </SelectCard.Body>
      </SelectCard.Root>
    </chakra.label>
  );
};

const Step1 = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <>
    <Text as="h1" textStyle={['2xl', null, '3xl']}>
      {title}
    </Text>
    <Text color="text.primary" mt={4} textStyle="l">
      {subtitle}
    </Text>
  </>
);

const Step2 = ({
  title,
  subtitle,
  image,
  name,
}: {
  title: string;
  subtitle: string;
  image: DatoImage;
  name: string;
}) => {
  const processedTitle = title?.replace('{name}', name);

  return (
    <>
      <Text as="h1" textStyle={['2xl', null, '3xl']}>
        {processedTitle}
      </Text>
      <Text mt={4} textStyle="l">
        {subtitle}
      </Text>
      <Box as="figure" w={[null, null, '50%']}>
        <Image data={image?.responsiveImage} />
      </Box>
    </>
  );
};

const GenericStepTitle = ({ title }: { title: string }) => (
  <Text as="h1" textStyle={['2xl', null, '3xl']}>
    {title}
  </Text>
);

const Step6 = ({
  campus,
  format,
  questionText,
  region,
  track,
  trackCode,
}: {
  campus: string;
  format: string;
  questionText: string;
  region: Region;
  track: string;
  trackCode: AvailableCourseTrack;
}) => {
  const badgeColor = courseToBackground[trackCode];

  return (
    <>
      <Flex
        bg={['white', null, 'none']}
        borderRadius={[16, null, 0]}
        boxShadow={['0 0 4px rgb(0 0 0 / 0.08)', null, 'unset']}
        direction="column"
        mb={[3, null, 6]}
        mt={[2, null, 3]}
        p={[2, null, 0]}
      >
        <Flex alignItems="center" gap={1} mb={[1.5, null, 2]}>
          <HexagonBadge
            color={`url(#${badgeColor})`}
            fill={`url(#${badgeColor})`}
            size="24px"
          />
          <Text textStyle="lBold">{track}</Text>
        </Flex>
        {!isMastersActive(region) && (
          <Flex alignItems="center" color="text.secondary" gap={0.5} mb={1}>
            <Icon as={Send} boxSize="20px" />
            <Text textStyle="mBold">{format}</Text>
          </Flex>
        )}
        <Flex alignItems="center" color="text.secondary" gap={0.5}>
          <Icon as={MapPin} boxSize="20px" />
          <Text textStyle="mBold">{campus}</Text>
        </Flex>
      </Flex>
      <Text mb={-2} textStyle={['mBold', null, 'lBold']}>
        {questionText}
      </Text>
    </>
  );
};

export const FormFull = (props: Props): ReactElement => {
  const {
    campuses,
    courses,
    fullApplyForm,
    preStudentPortalButton,
    thankYouButton,
    ...flexProps
  } = props;

  const {
    backButtonText,
    buttonText,
    errorMessage,
    timeoutErrorMessage,
    legalText,
    newsletterLabel,
    nextButtonText,
    phoneLabel,
    phonePlaceholder,
    title,
    subtitle,
    step1Cta,
    step2Title,
    step2Subtitle,
    step2Image,
    step3Title,
    step4Title,
    step5Title,
    step6Question,
    stepsTitleDynamic,
    step4FullTimeCard,
    step4PartTimeCard,
    step5ErrorMessage,
    step6MoreDates,
    step6RequiredMessage,
    thankYouTitle,
    thankYouText,
  } = fullApplyForm;

  const {
    params: { language, region },
    pageData: { gaCategory },
  } = usePageContext();
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);

  const [formEmail] = useSessionStorage<string>('formEmail');
  const [showThankYou, setShowThankYou] = useState(false);
  const [step, setStep] = useState(1);
  const [userName, setUserName] = useState('');
  const [trackCode, setTrackCode] = useState<AvailableCourseTrack | ''>('');
  const [track, setTrack] = useState('');
  const [format, setFormat] = useState<CourseFormat | ''>('');
  const [campus, setCampus] = useState('');

  // For Masters regions skip Step 4 and auto-select FT courses
  const displayStep = isMastersActive(region) && step > 3 ? step - 1 : step;
  const totalSteps = isMastersActive(region) ? '5' : '6';
  const defaultCourseFormat = isMastersActive(region) ? 'ft' : '';

  const stepsTitle = pipe(
    replace('{step}', displayStep.toString()),
    replace('{total}', totalSteps)
  )(stepsTitleDynamic);

  const formSchema = getFullApplySchema(fullApplyForm);

  const formMethods = useForm<FormData>({
    defaultValues: {
      email: formEmail,
      phone: getPhoneDefaults(region),
      selection: {
        campus: '',
        track: '',
        format: defaultCourseFormat,
      },
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    clearErrors,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = formMethods;
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error';
    message: string;
  } | null>();
  const [selectedCourseTrack, setSelectedCourseTrack] = useState<string>('');
  const [selectedCourseFormat, setSelectedCourseFormat] =
    useState<string>(defaultCourseFormat);
  const [selectedCourseCampus, setSelectedCourseCampus] = useState<string>('');
  const [isLoadingCohorts, setIsLoadingCohorts] = useState<boolean>(false);
  const [availableCohorts, setAvailableCohorts] = useState<WebCohort[]>([]);
  const [cohortLimit, setCohortLimit] = useState<number>(3);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isGeneratingLead, setIsGeneratingLead] = useState<boolean>(false);
  const [thankYouSession, setThankYouSession] = useThankYouSession();

  useEffect(() => {
    if (step === 2) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);

  const handleCourseTrackSelected = (courseTrack: AvailableCourseTrack) => {
    setSelectedCourseTrack(courseTrack);
    setValue('selection.track', courseTrack);
  };

  const handleCourseFormatSelected = (courseFormat: CourseFormat) => {
    setSelectedCourseFormat(courseFormat);
    setValue('selection.format', courseFormat);
  };

  const handleCourseCampusSelected = (courseCampus: string) => {
    setSelectedCourseCampus(courseCampus);
    setValue('selection.campus', courseCampus);
  };

  const handleCourseSelected = (cohortSalesforceId: string) => {
    setSelectedCourse(cohortSalesforceId);
  };

  const {
    getRadioProps: getRadioPropsCourseTrack,
    getRootProps: getRootPropsCourseTrack,
    isDisabled: isDisabledCourseTrack,
  } = useRadioGroup({
    name: 'selection.track',
    onChange: handleCourseTrackSelected,
    value: selectedCourseTrack,
  });
  const {
    getRadioProps: getRadioPropsCourseFormat,
    getRootProps: getRootPropsCourseFormat,
    isDisabled: isDisabledCourseFormat,
  } = useRadioGroup({
    name: 'selection.format',
    onChange: handleCourseFormatSelected,
    value: selectedCourseFormat,
  });
  const {
    getRadioProps: getRadioPropsCourseCampus,
    getRootProps: getRootPropsCourseCampus,
    isDisabled: isDisabledCourseCampus,
  } = useRadioGroup({
    name: 'selection.campus',
    onChange: handleCourseCampusSelected,
    value: selectedCourseCampus,
  });
  const {
    getRadioProps: getRadioPropsCourseSelection,
    getRootProps: getRootPropsCourseSelection,
    isDisabled: isDisabledCourseSelection,
  } = useRadioGroup({
    onChange: handleCourseSelected,
    value: selectedCourse,
  });
  const formRequest = useFormRequest();
  const [previousPageType] = useSessionStorage<string>('pageTypePrevious');
  const [, setGtmLocalStorage] = useGtmLocalStorage();
  const setCaptchaToken = (token: string) => setValue('cToken', token);

  const step5Complete = async (): Promise<boolean> => {
    const { selection } = getValues();
    const {
      track: selectionTrack,
      format: selectionFormat,
      campus: selectionCampus,
    } = selection;

    const validationResult = await trigger('selection.campus');
    if (!validationResult) {
      setFormFeedback({
        status: 'error',
        message: errors.selection?.campus?.message as string,
      });
      return false;
    }

    try {
      setIsLoadingCohorts(true);
      const cohortData = await fetch('/api/cohorts', {
        body: JSON.stringify({
          campus: selectionCampus,
          format: selectionFormat,
          language,
          region,
          track: selectionTrack,
        }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      const cohorts = (await cohortData.json()) as { result: WebCohort[] };
      const cohortsResult = cohorts.result;
      if (cohortsResult.length === 0) {
        setFormFeedback({
          status: 'error',
          message: step5ErrorMessage,
        });
        return false;
      }

      setAvailableCohorts(cohortsResult);
      setTrackCode(selectionTrack as AvailableCourseTrack);
      setTrack(commonKeys[language][selectionTrack]);
      setFormat(commonKeys[language][selectionFormat] as CourseFormat);
      setCampus(commonKeys[language][selectionCampus]);

      return true;
    } catch {
      return false;
    } finally {
      setIsLoadingCohorts(false);
    }
  };

  const step6Complete = async (): Promise<void> => {
    if (!selectedCourse) {
      setFormFeedback({ status: 'error', message: step6RequiredMessage });
      return;
    }

    setIsSubmitting(true);
    const formData = getValues();
    try {
      const response = await formRequest({
        data: {
          ...omit(['selection'], formData),
          courseId: selectedCourse,
          language,
        },
        formType: 'apply-full',
      });

      const formReferrer = sessionStorage.getItem('formReferrer') || 'default';
      const defaultEventCategory =
        previousPageType && previousPageType !== 'undefined'
          ? previousPageType
          : gaCategory;
      const { eventAction, eventCategory = defaultEventCategory } =
        contactFormTags[formReferrer];

      const {
        email,
        firstName,
        phone: { phoneNumber, country },
      } = formData;

      sendEvent({
        campus: selectedCourseCampus,
        course: selectedCourseTrack,
        email,
        eventAction,
        eventCategory,
        eventLabel: `${selectedCourseCampus}::${selectedCourseTrack}`,
        pageType: eventCategory,
      });

      setGtmLocalStorage({
        applicationData: {
          campus: selectedCourseCampus,
          course: selectedCourseTrack,
          phoneNumber,
          ...pick(['email', 'firstName', 'lastName'], formData),
        },
        countryName: country,
      });

      await setThankYouSession({ firstName, response });
      setShowThankYou(true);
    } catch (error) {
      logSeatBookingSubmitFailure(error, {
        form: 'apply-full',
        language,
        region,
        courseId: selectedCourse,
      });
      setFormFeedback({
        status: 'error',
        message: isFormSubmitTimeoutError(error)
          ? timeoutErrorMessage
          : errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = (stepNumber: number) => {
    setFormFeedback(null);
    if (stepNumber > 1) {
      const stepIncrement = isMastersActive(region) && stepNumber === 5 ? 2 : 1;
      setStep((currentStep) => currentStep - stepIncrement);
    }
  };

  const onStepComplete = async (stepNumber: number) => {
    setFormFeedback(null);
    let validationResult = false;
    let stepIncrement = 1;

    switch (stepNumber) {
      case 1: {
        const { firstName } = getValues();
        validationResult = await trigger([
          'cToken',
          'email',
          'firstName',
          'lastName',
        ]);
        if (validationResult) {
          setIsGeneratingLead(true);
          try {
            await formRequest({
              data: {
                ...omit(['phone', 'selection'], getValues()),
                language,
                source: 'Full Apply Form Step 1',
              },
              formType: 'nurturing',
            });
            clearErrors();
            setUserName(firstName);
          } catch {
            setFormFeedback({ status: 'error', message: errorMessage });
            validationResult = false;
          } finally {
            setIsGeneratingLead(false);
          }
        }
        break;
      }

      case 2: {
        validationResult = await trigger('phone');
        break;
      }

      case 3: {
        validationResult = await trigger('selection.track');
        if (!validationResult) {
          setFormFeedback({
            status: 'error',
            message: errors.selection?.track?.message as string,
          });
        } else if (isMastersActive(region)) {
          stepIncrement = 2;
        }
        break;
      }

      case 4: {
        validationResult = await trigger('selection.format');
        if (!validationResult)
          setFormFeedback({
            status: 'error',
            message: errors.selection?.format?.message as string,
          });
        break;
      }

      case 5: {
        validationResult = await step5Complete();
        break;
      }

      case 6: {
        await step6Complete();
        break;
      }

      default: {
        return null;
      }
    }

    if (validationResult) {
      setStep((currentStep) => currentStep + stepIncrement);
    }
  };

  return (
    <ModuleWrapper
      innerProps={{ direction: 'column', alignItems: 'center' }}
      moduleFormat={{
        background: 'light',
        spacingBottom: '40',
        spacingTop: '40',
      }}
      outerProps={{ flexGrow: 1 }}
    >
      <Card
        bg={['transparent', null, 'white.100']}
        boxShadow={[null, null, 'lg']}
        p={[null, null, 5]}
        w={['full', null, showThankYou ? '440px' : '680px']}
      >
        {!showThankYou && (
          <>
            <Box as="header" mb={4}>
              <Text
                color="brand.secondary"
                mb={1}
                textStyle="mBold"
                textTransform="uppercase"
              >
                {stepsTitle}
              </Text>
              {step === 1 && <Step1 subtitle={subtitle} title={title} />}
              {step === 2 && (
                <Step2
                  image={step2Image}
                  name={userName}
                  subtitle={step2Subtitle}
                  title={step2Title}
                />
              )}
              {step === 3 && <GenericStepTitle title={step3Title} />}
              {step === 4 && <GenericStepTitle title={step4Title} />}
              {step === 5 && <GenericStepTitle title={step5Title} />}
              {step === 6 && (
                <Step6
                  campus={campus}
                  format={format}
                  questionText={step6Question}
                  region={region}
                  track={track}
                  trackCode={trackCode as AvailableCourseTrack}
                />
              )}
            </Box>
            <Flex direction="column" {...flexProps} w="full">
              <FormProvider {...formMethods}>
                <form id="fullApplyForm" noValidate>
                  <Flex alignItems="center" direction="column">
                    {step === 1 && (
                      <>
                        <ContactDetailsFields
                          fields={pick(
                            [
                              'chooseBetween',
                              'completeFields',
                              'emailLabel',
                              'emailPlaceholder',
                              'firstNameLabel',
                              'firstNamePlaceholder',
                              'lastNameLabel',
                              'lastNamePlaceholder',
                              'phoneLabel',
                              'phonePlaceholder',
                            ],
                            fullApplyForm
                          )}
                          onGoogleClick={() => {
                            setIsGoogleSubmitting(true);
                            setFormFeedback(null);
                          }}
                          onGoogleError={() => {
                            setIsGoogleSubmitting(false);
                            setFormFeedback({
                              status: 'error',
                              message: errorMessage,
                            });
                          }}
                          onGoogleSuccess={async (googleData) => {
                            setValue('email', googleData.email);
                            setValue('firstName', googleData.firstName);
                            setValue('lastName', googleData.lastName);
                            const isValid = await trigger([
                              'email',
                              'firstName',
                              'lastName',
                            ]);
                            if (!isValid) return;

                            try {
                              await formRequest({
                                data: {
                                  ...omit(['phone', 'selection'], getValues()),
                                  language,
                                  source: 'Full Apply Form Step 1',
                                },
                                formType: 'nurturing-google',
                              });
                            } catch {
                              // Allow user to continue filling the form
                            }
                            clearErrors();
                            setIsGoogleSubmitting(false);

                            setUserName(googleData.firstName);
                            setStep((currentStep) => currentStep + 1);
                          }}
                          setFormValues={false}
                        />

                        <TurnstileFormToken
                          language={language}
                          onVerify={setCaptchaToken}
                        />
                        <CheckboxField
                          color="text.primary"
                          isRequired
                          label={newsletterLabel}
                          lineHeight={0}
                          ml="1px"
                          mt={1}
                          name="emailOptIn"
                          px={2}
                          py={1}
                          size="l"
                        />
                        <Button
                          isLoading={isGoogleSubmitting || isGeneratingLead}
                          mt={2}
                          onClick={() => onStepComplete(step)}
                        >
                          {step1Cta}
                        </Button>
                        <StructuredContent
                          alignItems="center"
                          color="text.secondary"
                          data={legalText}
                          mt={2}
                          textAlign="center"
                          textStyle="m"
                        />
                      </>
                    )}
                    {step === 2 && (
                      <PhoneField
                        error={
                          errors.phone?.message ||
                          errors.phone?.phoneNumber?.message
                        }
                        isRequired
                        label={phoneLabel}
                        name="phone"
                        placeholder={phonePlaceholder}
                      />
                    )}
                    {step === 3 && (
                      <Flex
                        {...getRootPropsCourseTrack()}
                        direction="column"
                        gap={2}
                        w="100%"
                      >
                        {courses.map(
                          (course: { name: string; code: string }) => (
                            <CourseTrackRadioCardField
                              courseName={course.name}
                              isDisabled={isDisabledCourseTrack}
                              key={course.name}
                              {...getRadioPropsCourseTrack({
                                value: course.code,
                              })}
                            />
                          )
                        )}
                      </Flex>
                    )}
                    {step === 4 && (
                      <Flex
                        {...getRootPropsCourseFormat()}
                        direction={['column', null, 'row']}
                        gap={[4, null, 2]}
                        w="100%"
                      >
                        <CourseFormatRadioCardField
                          cardData={step4PartTimeCard}
                          courseTrackName={selectedCourseTrack}
                          isDisabled={isDisabledCourseFormat}
                          {...getRadioPropsCourseFormat({
                            value: 'pt',
                          })}
                        />
                        <CourseFormatRadioCardField
                          cardData={step4FullTimeCard}
                          courseTrackName={selectedCourseTrack}
                          isDisabled={isDisabledCourseFormat}
                          {...getRadioPropsCourseFormat({
                            value: 'ft',
                          })}
                        />
                      </Flex>
                    )}
                    {step === 5 && (
                      <Flex
                        {...getRootPropsCourseCampus()}
                        direction="column"
                        gap={2}
                        my={2}
                        w="100%"
                      >
                        {pipe(
                          filter(
                            prop('isShownOnWebsite') as (
                              c: DatoCampus
                            ) => boolean
                          ),
                          map(
                            (currentCampus: {
                              name: string;
                              code: string;
                              country: string;
                            }) => (
                              <CourseCampusRadioCardField
                                courseCampusCode={currentCampus.code}
                                courseCampusCountry={currentCampus.country}
                                courseCampusName={currentCampus.name}
                                isDisabled={isDisabledCourseCampus}
                                key={currentCampus.code}
                                {...getRadioPropsCourseCampus({
                                  value: currentCampus.code,
                                })}
                              />
                            )
                          )
                        )(campuses)}
                      </Flex>
                    )}
                    {step === 6 && (
                      <>
                        <UnorderedList
                          {...getRootPropsCourseSelection()}
                          mb={2}
                          mx={0}
                          spacing={[1, null, 2]}
                          styleType="none"
                          w="full"
                        >
                          {availableCohorts
                            .slice(0, cohortLimit)
                            .map((cohort) => (
                              <ListItem key={cohort.id} w="full">
                                <CourseSelectionRadioCardField
                                  cohort={cohort}
                                  isDisabled={isDisabledCourseSelection}
                                  language={language}
                                  {...getRadioPropsCourseSelection({
                                    value: cohort.external_ids.salesforce,
                                  })}
                                />
                              </ListItem>
                            ))}
                        </UnorderedList>
                        {availableCohorts.length > 3 &&
                          cohortLimit < availableCohorts.length && (
                            <Button
                              leftIcon={Plus}
                              my={2}
                              onClick={() =>
                                setCohortLimit(
                                  (currentCohortLimit) => currentCohortLimit + 3
                                )
                              }
                              variant="tertiary"
                            >
                              {step6MoreDates}
                            </Button>
                          )}
                      </>
                    )}
                    {formFeedback?.status && (
                      <FormFeedback mt={2} {...formFeedback} />
                    )}
                    {step > 1 && (
                      <Flex gap={1} my={[4, null, 3]} w="full">
                        <Button
                          leftIcon={ArrowLeft}
                          onClick={() => goBack(step)}
                          px={[1, null, 5]}
                          variant="secondary"
                          w="full"
                        >
                          {backButtonText}
                        </Button>
                        <Button
                          {...(step !== 6 && { rightIcon: ArrowRight })}
                          isLoading={isLoadingCohorts || isSubmitting}
                          onClick={() => onStepComplete(step)}
                          px={[1, null, 5]}
                          variant="primary"
                          w="full"
                        >
                          {step === 6 ? buttonText : nextButtonText}
                        </Button>
                      </Flex>
                    )}
                  </Flex>
                </form>
              </FormProvider>
            </Flex>
          </>
        )}
        {showThankYou && (
          <FormThankYou
            bg={[null, null, 'background.white']}
            borderRadius={[null, null, '2xl']}
            button={thankYouButton}
            id="fullApplySuccess"
            preStudentPortalButton={preStudentPortalButton}
            text={thankYouText}
            textAlign="center"
            thankYouSession={thankYouSession}
            title={thankYouTitle}
            w="full"
          />
        )}
      </Card>
    </ModuleWrapper>
  );
};
